import React, { Suspense } from "react";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

import { TodoAssigneesPopover } from "./TodoAssigneesPopover";

import { TodoEditAssigneesPopoverMutation } from "./__generated__/TodoEditAssigneesPopoverMutation.graphql";
import { TodoEditAssigneesPopoverQuery } from "./__generated__/TodoEditAssigneesPopoverQuery.graphql";
import { TodoEditAssigneesPopoverRolesMutation } from "./__generated__/TodoEditAssigneesPopoverRolesMutation.graphql";

type TodoEditAssigneesPopoverProps = {
  todoId: string;
  visible: boolean;
  onClose: () => void;
  setStatus: (key: string, status: "saving" | "saved" | "error") => void;
};

export function TodoEditAssigneesPopover(props: TodoEditAssigneesPopoverProps) {
  return (
    <Suspense fallback={null}>
      <TodoEditAssigneesPopoverInner {...props} />
    </Suspense>
  );
}

function TodoEditAssigneesPopoverInner(props: TodoEditAssigneesPopoverProps) {
  const { currentEmployeeId } = usePersistedEmployeeId();
  const data = useLazyLoadQuery<TodoEditAssigneesPopoverQuery>(
    graphql`
      query TodoEditAssigneesPopoverQuery($id: ID!, $visible: Boolean!) {
        todo(id: $id) @include(if: $visible) {
          assignableEmployees(first: 200) {
            ...TodoAssigneesPopover_EmployeeConnection
            pageInfo {
              hasNextPage
              totalCount
              endCursor
              lastCursor
            }
          }

          assigned {
            employee {
              id
            }
          }
          assignableRoles {
            ...TodoAssigneesPopover_Roles
          }
          assignedRoles {
            role {
              id
            }
          }
        }
      }
    `,
    {
      id: props.todoId,
      visible: props.visible,
    },
    {
      fetchPolicy: "store-and-network",
    }
  );
  const [updateAssignees] = useMutation<TodoEditAssigneesPopoverMutation>(
    graphql`
      mutation TodoEditAssigneesPopoverMutation(
        $id: ID!
        $assignees: [ID!]!
        $employeeId: ID
      ) @raw_response_type {
        todo(todoId: $id, employeeId: $employeeId) {
          updateAssignedEmployees(updateAssignedEmployeeIds: $assignees) {
            id
            assigned {
              employee {
                id
              }
            }
          }
        }
      }
    `
  );

  const [updateAssignedRoles] =
    useMutation<TodoEditAssigneesPopoverRolesMutation>(graphql`
      mutation TodoEditAssigneesPopoverRolesMutation(
        $id: ID!
        $roleIds: [ID!]!
        $employeeId: ID
      ) @raw_response_type {
        todo(todoId: $id, employeeId: $employeeId) {
          updateAssignedRoles(updateAssignedRoleIds: $roleIds) {
            id
            assignedRoles {
              role {
                id
              }
            }
          }
        }
      }
    `);

  if (!data.todo?.assignableEmployees) {
    return null;
  }
  return (
    <TodoAssigneesPopover
      key={props.visible ? "visible" : "hidden"}
      employeeConnection={data.todo?.assignableEmployees ?? null}
      visible={props.visible}
      onClose={props.onClose}
      assignEmployeeIds={
        data.todo?.assigned?.flatMap(
          (assignedEmployee) => assignedEmployee.employee?.id ?? []
        ) ?? []
      }
      setAssignEmployeeIds={(selectedAssigneeIds) => {
        updateAssignees({
          variables: {
            id: props.todoId,
            assignees: selectedAssigneeIds,
            employeeId: currentEmployeeId,
          },
          optimisticResponse: {
            todo: {
              updateAssignedEmployees: {
                id: props.todoId,
                assigned: selectedAssigneeIds.map((id) => ({
                  id: id + "-optimistic",
                  employee: { id },
                })),
              },
            },
          },
        });
      }}
      assignRoleIds={
        data.todo?.assignedRoles?.flatMap(
          (assignedRole) => assignedRole.role?.id ?? []
        ) ?? []
      }
      setAssignRoleIds={(selectedRoleIds) => {
        updateAssignedRoles({
          variables: {
            id: props.todoId,
            roleIds: selectedRoleIds,
            employeeId: currentEmployeeId,
          },
          optimisticResponse: {
            todo: {
              updateAssignedRoles: {
                id: props.todoId,
                assignedRoles: selectedRoleIds.map((id) => ({
                  id: id + "-optimistic",
                  role: { id },
                })),
              },
            },
          },
        });
      }}
      roles={data.todo?.assignableRoles ?? null}
    />
  );
}
